import classnames from 'clsx';
import type { ReactElement } from 'react';
import React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import { GameList, GameTile, GameViewToggle, useHomeGamesWidget } from '@swf/poker';

import type { HGPokerGameTile } from '../../types/common';
import { GameView, TileType } from '../../types/common';
import DeleteGameButton from '../GameTile/DeleteGame/DeleteGameButton';
import GameTileSkeleton from '../Shared/GameTileSkeleton/GameTileSkeleton';

import styles from './GameTileSection.scss';

export interface Props {
  title: ReactElement;
  games: HGPokerGameTile[];
  type: TileType;
  source: string;
  loading?: boolean;
}

const GameTileSection: React.FC<Props> = ({ title, games, type, source, loading }) => {
  const { clubId } = useParams<{ clubId: string }>();
  const { widgetSelect } = useHomeGamesWidget();
  const isMobileDevice = useSelector(select.device.isMobile);
  const isCashGamesActive = useSelector(widgetSelect.isCashGameActive);
  const isTournsActive = useSelector(widgetSelect.isTournsActive);
  const toggleId = `${type}-${source}`;
  const viewStoreState = useSelector((rootState: RootState) =>
    widgetSelect.localStorageItem(rootState, `gameTileView[${toggleId}]`)
  );
  const viewState = isMobileDevice ? GameView.Grid : viewStoreState || GameView.Grid;
  const showListView = viewState === GameView.List;

  return (
    <section className={classnames(styles.container, { [styles.containerMobile]: isMobileDevice })}>
      <div className={styles.title}>
        <p>{title}</p>
        {type !== TileType.Recent && <DeleteGameButton games={games} />}
        {!isMobileDevice && <GameViewToggle id={toggleId} />}
      </div>
      {loading ? (
        <GameTileSkeleton games={games} type={viewState as GameView} />
      ) : showListView ? (
        <div>
          <GameList games={games} tileType={type} clubId={clubId} />
        </div>
      ) : (
        <div className={styles.gamesInnerContainer} data-testid={`${type}-game-container`}>
          {games?.map((game: HGPokerGameTile) => (
            <GameTile
              key={game.id}
              {...game}
              isCashGamesActive={isCashGamesActive}
              isTournsActive={isTournsActive}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default GameTileSection;
